import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';

import { PatientDocument } from '../../../../../core/models/document/document.model';
import { DocumentService } from '../../../../../core/services/document/document.service';
import { DocumentType } from '../../../../../../enums/document-type.enum';

@Component({
  selector: 'app-document-preview',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './document-preview.component.html',
  styleUrl: './document-preview.component.scss',
})
export class DocumentPreviewComponent {
  @Input() document!: PatientDocument;

  @Output() closeRequested = new EventEmitter<void>();
  @Output() downloadRequested = new EventEmitter<PatientDocument>();

  previewUrl: SafeResourceUrl | null = null;
  isLoading = true;
  hasError = false;

  constructor(
    private documentService: DocumentService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.loadPreview();
  }

  get isPdf(): boolean {
    return this.document.fileType === DocumentType.PDF;
  }

  get isImage(): boolean {
    return [DocumentType.PNG, DocumentType.JPG, DocumentType.JPEG].includes(
      this.document.fileType
    );
  }

  get isWord(): boolean {
    return [DocumentType.DOC, DocumentType.DOCX].includes(
      this.document.fileType
    );
  }

  private loadPreview(): void {
    this.isLoading = true;
    this.hasError = false;

    this.documentService.getDocumentPreviewUrl(this.document.id).subscribe({
      next: (blob) => {
        const pdfBlob = this.isPdf
          ? new Blob([blob], { type: 'application/pdf' })
          : blob;

        const objectUrl = URL.createObjectURL(pdfBlob);
        const finalUrl = this.isPdf
          ? `${objectUrl}#toolbar=0&navpanes=0`
          : objectUrl;
        this.previewUrl =
          this.sanitizer.bypassSecurityTrustResourceUrl(finalUrl);
        this.isLoading = false;
      },
      error: () => {
        this.hasError = true;
        this.isLoading = false;
      },
    });
  }

  onClose(): void {
    if (this.previewUrl) {
      const url = (this.previewUrl as any)
        .changingThisBreaksApplicationSecurity;
      if (url) {
        URL.revokeObjectURL(url);
      }
    }
    this.closeRequested.emit();
  }

  onDownload(): void {
    this.downloadRequested.emit(this.document);
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('preview-modal')) {
      this.onClose();
    }
  }
}
